import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import { FaCheck } from "react-icons/fa";
import Button from "./Button";
import LabelInput from "./labelInput";
import FieldStack from "./FieldStack";

const normalizeUkPostcode = (value = "") => {
  const compactPostcode = (value || "")
    .toString()
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  return compactPostcode.length > 3
    ? `${compactPostcode.slice(0, -3)} ${compactPostcode.slice(-3)}`
    : compactPostcode;
};

const isValidUkPostcode = (value = "") =>
  /^(GIR 0AA|[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2})$/.test(
    normalizeUkPostcode(value)
  );

const AddressLookup = ({
  register,
  watch,
  control,
  setValue,
  trigger,
  errors,
  labelHouse = "House name or number",
  labelPostCode = "Postcode",
  STATE,
  scope = "driver",
  houseField = "address",
  postcodeField = "postcode",
  lookUpAddressField = "lookUpAddress",
  manualAddressField = "manualAddress",
  manualSecondAddressField = "manualSecondAddress",
  cityField = "city",
  countyField = "county",
  autoLookup = false,
}) => {
  const [addressData, setAddressData] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const [errorAddress, setErrorAddress] = useState("");
  const [loader, setLoader] = useState(false);
  const lookupRequestId = useRef(0);
  const lastAutoLookupKey = useRef("");
  const preserveConfirmedManualAddress = useRef(false);

  const address = watch(houseField);
  const postcode = watch(postcodeField);
  const selectedAddress = watch(lookUpAddressField);
  const addressOptionField = `${lookUpAddressField}Option`;
  const selectedAddressText = [
    selectedAddress?.house || selectedAddress?.houseNo,
    selectedAddress?.street,
    selectedAddress?.city,
    selectedAddress?.county,
    selectedAddress?.postcode,
  ]
    .filter((part) => part?.toString().trim())
    .join(", ");

  const getScopedKey = (base) => {
    const normalizedScope =
      scope?.toLowerCase?.() === "garage" ? "Garage" : "Driver";
    const scopedKey = `${base}${normalizedScope}`;
    return STATE?.[scopedKey] !== undefined ? scopedKey : base;
  };

  const enterAddressKey = getScopedKey("enterAddress");
  const showLookupBtnKey = getScopedKey("showAddressLookupbtn");
  const lookupAddressKey = getScopedKey("lookupAddress");

  const setEnterAddressKey = `set${enterAddressKey[0].toUpperCase()}${enterAddressKey.slice(
    1
  )}`;
  const setShowLookupBtnKey = `set${showLookupBtnKey[0].toUpperCase()}${showLookupBtnKey.slice(
    1
  )}`;
  const setLookupAddressKey = `set${lookupAddressKey[0].toUpperCase()}${lookupAddressKey.slice(
    1
  )}`;
  const setLookupAddress = STATE?.[setLookupAddressKey] ?? STATE?.setLookupAddress;

  const enterAddress = STATE?.[enterAddressKey] ?? false;
  const showLookupBtn = STATE?.[showLookupBtnKey] ?? true;

  // Options for address selection
  const options = allAddresses.map((address) => ({
    value: JSON.stringify(address),
    label: `${address.house}, ${address.street}, ${address.city}, ${address.postcode}`,
  }));

  const handleAddressLookup = useCallback(async (requestId) => {
    const currentRequestId = requestId || ++lookupRequestId.current;
    const normalizedAddress = address?.toString().trim();
    const normalizedPostcode = normalizeUkPostcode(postcode);

    setValue(lookUpAddressField, undefined);
    setValue(addressOptionField, null);
    setLookupAddress?.(null);
    setAddressData(null);
    setAllAddresses([]);
    setErrorAddress("");

    if (!normalizedAddress) {
      setErrorAddress("Please enter a house name or number.");
      return;
    }
    if (!isValidUkPostcode(normalizedPostcode)) {
      trigger(postcodeField);
      return;
    }

    setLoader(true);

    try {
      const response = await axios.get("/api/address-lookup", {
        params: { postcode: normalizedPostcode, address: normalizedAddress },
      });

      if (currentRequestId !== lookupRequestId.current) return;

      if (response.status !== 200) {
        throw new Error(
          `Network response was not ok: ${response.statusText ||
            response.status}`
        );
      }

      const data = response.data;

      if (data.addressFound) {
        setAddressData(data.addressFound);
        setAllAddresses([]);
        setValue(lookUpAddressField, data.addressFound);
        setLookupAddress?.(data.addressFound);
        setErrorAddress("");
      } else if (Array.isArray(data.addresses) && data.addresses.length > 0) {
        setAllAddresses(data.addresses);
      } else {
        throw new Error("No addresses found. Check the house number and postcode.");
      }
    } catch (error) {
      if (currentRequestId !== lookupRequestId.current) return;
      setErrorAddress(error.response?.data?.message || error.message);
      setValue(lookUpAddressField, undefined);
      setLookupAddress?.(null);
      setAllAddresses([]);
      setAddressData(null);
    } finally {
      if (currentRequestId === lookupRequestId.current) setLoader(false);
    }
  }, [address, postcode, setValue, lookUpAddressField, addressOptionField, setLookupAddress, trigger, postcodeField]);

  const handleSelectChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      const selectedValue = JSON.parse(selectedOption.value);

      // Set the selected address data
      setAddressData(selectedValue);

      // Update the form value
      setValue(lookUpAddressField, selectedValue);

      // Clear any error messages
      setErrorAddress("");

      // Retrigger validation for "lookUpAddress"
      trigger(lookUpAddressField);
    }
  };

  const handleManualOrLookupAdress = () => {
    const setEnter = STATE?.[setEnterAddressKey] ?? STATE?.setEnterAddress;
    const setShow = STATE?.[setShowLookupBtnKey] ?? STATE?.setShowAddressLookupbtn;
    if (!enterAddress) {
      setEnter?.(true);
      setShow?.(false);
    } else {
      setEnter?.(false);
      setShow?.(true);
    }
  };

  const isRequiredValue = (value) =>
    value?.toString().trim() ? true : "Required";

  const handleConfirmAddress = async () => {
    const isManualAddressValid = await trigger([
      manualAddressField,
      cityField,
      countyField,
    ]);

    if (!isManualAddressValid) {
      setValue(lookUpAddressField, undefined);
      return;
    }

    const manualAddress = {
      house: watch(manualAddressField)?.toString().trim(),
      street: watch(manualSecondAddressField)?.toString().trim() || "",
      city: watch(cityField)?.toString().trim(),
      county: watch(countyField)?.toString().trim(),
      postcode: watch(postcodeField),
    };
    preserveConfirmedManualAddress.current = autoLookup;
    setAddressData(manualAddress);
    setValue(lookUpAddressField, manualAddress, { shouldValidate: true });
    setLookupAddress?.(manualAddress);
    setErrorAddress("");
    trigger(lookUpAddressField);
    const setEnter = STATE?.[setEnterAddressKey] ?? STATE?.setEnterAddress;
    const setShow = STATE?.[setShowLookupBtnKey] ?? STATE?.setShowAddressLookupbtn;
    setEnter?.(false);
    setShow?.(true);
  };

  useEffect(() => {
    if (
      autoLookup &&
      !enterAddress &&
      preserveConfirmedManualAddress.current
    ) {
      preserveConfirmedManualAddress.current = false;
      return;
    }

    setAddressData(null);
    setAllAddresses([]);

    if (!autoLookup) return;

    const normalizedAddress = address?.toString().trim();
    const normalizedPostcode = normalizeUkPostcode(postcode);
    const hasLookupInputs =
      !enterAddress &&
      normalizedAddress &&
      isValidUkPostcode(normalizedPostcode);

    if (!hasLookupInputs) {
      ++lookupRequestId.current;
      setLoader(false);
      setErrorAddress("");
      setValue(lookUpAddressField, undefined);
      setValue(addressOptionField, null);
      setLookupAddress?.(null);
      lastAutoLookupKey.current = "";
      return;
    }

    const lookupKey = `${normalizedAddress.toLowerCase()}|${normalizedPostcode}`;
    if (lookupKey === lastAutoLookupKey.current) return;

    const requestId = ++lookupRequestId.current;
    setLoader(false);
    setErrorAddress("");
    setValue(lookUpAddressField, undefined);
    setValue(addressOptionField, null);
    setLookupAddress?.(null);
    lastAutoLookupKey.current = lookupKey;
    handleAddressLookup(requestId);
  }, [
    address,
    postcode,
    enterAddress,
    autoLookup,
    setValue,
    lookUpAddressField,
    addressOptionField,
    setLookupAddress,
    handleAddressLookup,
  ]);

  return (
    <FieldStack className="tw-w-full">
      {!enterAddress && (
        <>
          <LabelInput
            id={houseField}
            label={labelHouse}
            type="text"
            placeholder="e.g 12"
            register={register(houseField, { required: "Required" })}
            errors={errors?.[houseField]}
            watch={address}
            trigger={trigger}
          />

          <LabelInput
            id={postcodeField}
            label={labelPostCode}
            type="text"
            placeholder="e.g M50 3YJ"
            register={register(postcodeField, {
              required: "Required",
              ...(autoLookup && {
                minLength: {
                  value: 5,
                  message: "Postcode must be at least 5 characters.",
                },
                maxLength: {
                  value: 8,
                  message: "Postcode must be no more than 8 characters.",
                },
                validate: (value) =>
                  !value ||
                  isValidUkPostcode(value) ||
                  "Enter a valid UK postcode.",
              }),
            })}
            errors={errors?.[postcodeField]}
            watch={postcode}
            trigger={trigger}
            minLength={autoLookup ? 5 : undefined}
            maxLength={autoLookup ? 8 : undefined}
          />

          {loader && (
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-dis-text-muted"
              role="status"
              aria-live="polite"
            >
              <FaSpinner className="tw-h-4 tw-w-4 tw-animate-spin tw-text-dis-deep-blue" aria-hidden="true" />
              <span>Looking up address…</span>
            </div>
          )}

          {errorAddress && (
            <span className="tw-text-sm tw-text-dis-error">{errorAddress}</span>
          )}

          {allAddresses.length > 0 && (
            <LabelInput
              id={addressOptionField}
              label="Select address"
              type="select"
              control={control}
              options={options}
              placeholder="Select address"
              errors={errors?.[addressOptionField]}
              watch={watch(addressOptionField)}
              trigger={trigger}
              onChange={handleSelectChange}
            />
          )}

          <LabelInput
            id={lookUpAddressField}
            className="tw-hidden"
            name={lookUpAddressField}
            type="text"
            register={register(lookUpAddressField, {
              validate: (value) =>
                !postcode ||
                value !== "" ||
              "This field is required if Vehicle Registration has a value",
            })}
          />
        </>
      )}

      {enterAddress && (
        <>
          <LabelInput
            id={manualAddressField}
            label="Address line 1"
            type="text"
            placeholder="Enter address 1"
            register={register(manualAddressField, {
              required: "Required",
              validate: isRequiredValue,
            })}
            errors={errors?.[manualAddressField]}
            watch={watch(manualAddressField)}
            trigger={trigger}
          />
          <LabelInput
            id={manualSecondAddressField}
            label="Address line 2 (optional)"
            type="text"
            placeholder="Enter address 2"
            register={register(manualSecondAddressField)}
            trigger={trigger}
          />
          <LabelInput
            id={cityField}
            label="City"
            type="text"
            placeholder="e.g London"
            register={register(cityField, {
              required: "Required",
              validate: isRequiredValue,
            })}
            errors={errors?.[cityField]}
            watch={watch(cityField)}
            trigger={trigger}
          />
          <LabelInput
            id={countyField}
            label="County"
            type="text"
            placeholder="e.g Greater London"
            register={register(countyField, {
              required: "Required",
              validate: isRequiredValue,
            })}
            errors={errors?.[countyField]}
            watch={watch(countyField)}
            trigger={trigger}
          />
        </>
      )}

      {selectedAddress && (
        <div
          className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-dis-border tw-bg-dis-info-panel tw-py-4 tw-pl-4 tw-pr-2.5"
          role="status"
          aria-label="Your address"
        >
          <div className="tw-min-w-0">
            <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-dis-navy">
              Your address
            </p>
            <p className="tw-mb-0 tw-mt-1 tw-break-words tw-text-sm tw-leading-[1.5] tw-text-dis-text-2">
              {selectedAddressText}
            </p>
          </div>
          <span className="tw-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-success tw-text-white" aria-hidden="true">
            <FaCheck className="tw-h-2.5 tw-w-2.5" />
          </span>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-between max-[375px]:tw-grid max-[375px]:tw-grid-cols-1 max-[375px]:tw-gap-1.5">
        {(!autoLookup || enterAddress) &&
          (showLookupBtn && !enterAddress ? (
            <Button
              type="button"
              size="small"
              loading={loader}
              loadingText="Looking up..."
              className="max-[375px]:tw-w-full"
              onClick={() => handleAddressLookup()}
            >
              Look up address
            </Button>
          ) : (
            <Button
              type="button"
              size="small"
              className="max-[375px]:tw-w-full"
              onClick={handleConfirmAddress}
            >
              Confirm Address
            </Button>
          ))}

        <div className="tw-text-right max-[375px]:tw-text-left">
          <Button
            onClick={handleManualOrLookupAdress}
            type="button"
            variant="soft"
            size="small"
            className="max-[375px]:tw-w-full"
          >
            {!enterAddress ? "Enter address manually" : "Automatic lookup"}
          </Button>
        </div>
      </div>
    </FieldStack>
  );
};

export default AddressLookup;
